import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { FaCheck, FaLocationArrow, FaStar } from "react-icons/fa";
import { db } from "../Configuration/firebase";
import { getCurrentPosition } from "../utils/proximity";
import { reverseGeocode } from "../utils/geocode";

const LOCATED_ID = "__located__";

// Reusable location picker, modelled on the My Addresses page: a search box, the
// user's saved addresses (favourites first, then alphabetical), an always-visible
// box showing the picked address (empty when none is picked yet), and a "use my
// current location" button at the bottom. Any action buttons the parent passes as
// children render between the box and the location button (so e.g. the event
// screen's Save/Cancel sit above "use my current location"). Reports the current
// pick to the parent via onSelect({ addressTitle, address }) (null when none).
const AddressPicker = ({
  uid,
  onSelect,
  autoSelectFirst = true,
  previewLabel = "Sharing this address",
  children,
}) => {
  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [locatedAddress, setLocatedAddress] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locateAccuracy, setLocateAccuracy] = useState(null);
  const [error, setError] = useState("");
  // The editable address text shown in the box. Initialised from the current
  // pick (tap/locate/search) but freely editable, so the host can correct a
  // located address before saving.
  const [addressText, setAddressText] = useState("");

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "users", uid, "addresses"));
        if (!cancelled) {
          setAddresses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        console.error("Error loading addresses:", err);
        if (!cancelled) setError("Couldn't load your saved addresses.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  // Clear the manual pick whenever the search text changes, mirroring the My
  // Addresses page - the box re-fills when the filter narrows to one match or
  // the user taps a result.
  useEffect(() => {
    setSelectedId(null);
  }, [searchTerm]);

  const handleLocate = async () => {
    setLocating(true);
    setError("");
    try {
      const pos = await getCurrentPosition({ highAccuracy: true });
      if (!pos) {
        throw new Error(
          "Couldn't get your location. Check location permission and try again.",
        );
      }
      const address = await reverseGeocode(pos);
      setLocatedAddress({
        id: LOCATED_ID,
        Title: "Current location",
        Address: address,
      });
      setLocateAccuracy(typeof pos.accuracy === "number" ? pos.accuracy : null);
      setSelectedId(LOCATED_ID);
    } catch (err) {
      console.error("Locate me failed:", err);
      setError(err.message || "Couldn't detect your location.");
    } finally {
      setLocating(false);
    }
  };

  // Favourites first, then alphabetical by title - same order as the My
  // Addresses page.
  const sorted = [...addresses].sort((a, b) => {
    if (!!a.isFavorite !== !!b.isFavorite) return a.isFavorite ? -1 : 1;
    return (a.Title || "").localeCompare(b.Title || "");
  });
  const term = searchTerm.trim().toLowerCase();
  const isSearching = term.length > 0;
  const filtered = isSearching
    ? sorted.filter(
        (a) =>
          (a.Title || "").toLowerCase().includes(term) ||
          (a.Address || "").toLowerCase().includes(term),
      )
    : sorted;
  const options = locatedAddress ? [locatedAddress, ...filtered] : filtered;

  // The picked address: an explicit tap/locate wins; else a single search match;
  // else (only when autoSelectFirst) the first address; else nothing.
  const tapped =
    locatedAddress && selectedId === LOCATED_ID
      ? locatedAddress
      : addresses.find((a) => a.id === selectedId) || null;
  const singleFiltered = filtered.length === 1 ? filtered[0] : null;
  const selectedAddress =
    tapped ||
    singleFiltered ||
    (!isSearching && autoSelectFirst ? filtered[0] : null) ||
    null;

  // Reset the editable text to the freshly-picked address whenever the pick
  // changes (a different row, a locate, a single search match) - but not on
  // every keystroke, so the host's edits to the current pick stick.
  useEffect(() => {
    setAddressText(selectedAddress ? selectedAddress.Address : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress ? selectedAddress.id : null]);

  // Report the (possibly edited) address to the parent.
  useEffect(() => {
    const text = addressText.trim();
    onSelect(
      text
        ? {
            addressTitle: selectedAddress ? selectedAddress.Title : "",
            address: addressText,
          }
        : null,
    );
    // onSelect is a stable setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressText, selectedAddress ? selectedAddress.id : null]);

  const renderOption = (a) => {
    const isSelected = selectedAddress && selectedAddress.id === a.id;
    const isLocated = a.id === LOCATED_ID;
    return (
      <button
        key={a.id}
        type="button"
        onClick={() => setSelectedId(a.id)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          width: "100%",
          textAlign: "left",
          padding: "12px 14px",
          marginBottom: "8px",
          borderRadius: "10px",
          border: isSelected
            ? "2px solid #4CAF50"
            : "1px solid var(--border-color, #eee)",
          backgroundColor: isSelected
            ? "rgba(76, 175, 80, 0.10)"
            : "var(--chip-bg, #f6f6f6)",
          color: "var(--text-primary, #1a1a1a)",
          cursor: "pointer",
          fontSize: "15px",
          fontWeight: isSelected ? 700 : 500,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {isLocated && <FaLocationArrow size={13} color="#007bff" />}
          {a.isFavorite && <FaStar size={13} color="#f5a623" />}
          {a.Title}
        </span>
        {isSelected && <FaCheck color="#4CAF50" size={15} />}
      </button>
    );
  };

  return (
    <>
      {/* Search */}
      <input
        type="text"
        autoComplete="off"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search addresses"
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "10px 15px",
          borderRadius: "20px",
          border: "1px solid var(--border-color, #ccc)",
          backgroundColor: "var(--input-bg, #eef3f8)",
          color: "var(--text-primary, #000)",
          marginBottom: "10px",
        }}
      />

      {/* Address list */}
      <div
        style={{ maxHeight: "240px", overflowY: "auto", marginBottom: "12px" }}
      >
        {options.length === 0 ? (
          <p
            style={{
              fontSize: "13px",
              color: "var(--text-secondary, #777)",
              margin: "4px 2px",
            }}
          >
            {isSearching
              ? "No matching addresses."
              : "No saved addresses yet - use your current location below, or add an address first."}
          </p>
        ) : (
          options.map((a) => renderOption(a))
        )}
      </div>

      {error && <p style={{ color: "red", fontSize: "13px" }}>{error}</p>}

      {/* Always-visible box: the picked address, or empty when none is picked. */}
      <div
        style={{
          padding: "12px 14px",
          borderRadius: "10px",
          backgroundColor: "var(--chip-bg, #f0f0f0)",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            fontSize: "12px",
            color: "var(--text-secondary, #777)",
            marginBottom: "2px",
          }}
        >
          {previewLabel}
        </div>
        {/* Editable: the host can correct the picked/located address before
            saving (e.g. fix the street number or add a unit). */}
        <textarea
          value={addressText}
          onChange={(e) => setAddressText(e.target.value)}
          placeholder="No address selected - pick one above, or type it here"
          rows={2}
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: "2px",
            padding: "8px 10px",
            borderRadius: "8px",
            border: "1px solid var(--border-color, #ccc)",
            backgroundColor: "var(--input-bg, #fff)",
            color: "var(--text-primary, #1a1a1a)",
            fontSize: "14px",
            fontFamily: "inherit",
            resize: "vertical",
          }}
        />
      </div>

      {/* Parent actions (e.g. Save / Cancel) sit here, above the locate button. */}
      {children}

      {/* Use my current location - fills the box above with the detected
          address. Placed last so it sits under the parent's action buttons. */}
      <button
        type="button"
        onClick={handleLocate}
        disabled={locating}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
          padding: "12px 14px",
          marginTop: "12px",
          borderRadius: "10px",
          border: "1px dashed var(--border-color, #aaa)",
          backgroundColor: "transparent",
          color: "var(--text-primary, #1a1a1a)",
          cursor: locating ? "default" : "pointer",
          fontSize: "15px",
        }}
      >
        <FaLocationArrow size={14} color="#007bff" />
        {locating ? "Detecting your location..." : "Use my current location"}
      </button>
      <p
        style={{
          fontSize: "11px",
          color: "var(--text-secondary, #999)",
          margin: "6px 2px 0",
          lineHeight: 1.3,
        }}
      >
        {locateAccuracy != null
          ? `Approximate location (±${Math.round(locateAccuracy)} m) - please double-check the address.`
          : "Approximate - please double-check the address."}
      </p>
    </>
  );
};

export default AddressPicker;
